import { BaseWebComponentView } from '../../../../framework/BaseWebComponentView';
import { GzTitleWebComponent } from '../../../../ui/gzTitle';
import { GzLabeledContentWebComponent } from '../../../../ui/gzLabeledContent';
import { FontSizes, TemplateState } from '../../../../types/common/types';
import { GzButtonWebComponent } from '../../../../ui/gzButton';
import { FormGroup } from '../../../../ui/gzForm/formGroup/form-group';
import { Subscription } from 'rxjs';
import { GzFormListWebComponent } from '../../../../ui/gzForm/formList';
import { RequiredValidator } from '../../../../ui/gzForm/validators/required.validator';
import { MaxLengthValidator } from '../../../../ui/gzForm/validators/max-length.validator';
import { IMConstants } from '../../../common/utils/IMConstants';
import { GzLinkWebComponent } from '../../../../ui/gzLinkComponent';
import { toggleVisibility } from '../../../../utils/htmlElement.utils';

export class IntegrityMonitorDefaultRuleConfigurationView extends BaseWebComponentView {
  public formGroup!: FormGroup;
  private formGroupSubscription: Subscription | null = null;

  public dom: {
    $wrapper: HTMLElement;
    $formWrapper: HTMLElement;
    $configurationSection: HTMLElement;
    $configurationFormFields: GzFormListWebComponent;

    $generalTitle: GzTitleWebComponent;
    $detailsTitle: GzTitleWebComponent;
    $configurationTitle: GzTitleWebComponent;
    $assignationTitle: GzTitleWebComponent;

    $learnMoreLink: GzLinkWebComponent;

    $ruleName: GzLabeledContentWebComponent;
    $ruleDescription: GzLabeledContentWebComponent;
    $ruleSeverity: GzLabeledContentWebComponent;
    $ruleType: GzLabeledContentWebComponent;
    $ruleCreatedOn: GzLabeledContentWebComponent;
    $ruleModifiedOn: GzLabeledContentWebComponent;

    $ruleEntityTypes: GzLabeledContentWebComponent;
    $mitreIds: GzLabeledContentWebComponent;

    $policies: GzLabeledContentWebComponent;
    $ruleSets: GzLabeledContentWebComponent;

    $saveButton: GzButtonWebComponent;
    $cancelButton: GzButtonWebComponent;
  };

  getDomElements() {
    this.dom = {
      $wrapper: this.shadow.querySelector('.wrapper'),
      $formWrapper: this.shadow.querySelector('.form-wrapper'),
      $configurationSection: this.shadow.querySelector('#configuration-section'),
      $configurationFormFields: this.shadow.querySelector('#configuration-form-fields'),

      $generalTitle: this.shadow.querySelector('#general-title'),
      $detailsTitle: this.shadow.querySelector('#details-title'),
      $configurationTitle: this.shadow.querySelector('#configuration-title'),
      $assignationTitle: this.shadow.querySelector('#assignation-title'),

      $learnMoreLink: this.shadow.querySelector('#learn-more'),

      $ruleName: this.shadow.querySelector('#name'),
      $ruleDescription: this.shadow.querySelector('#description'),
      $ruleSeverity: this.shadow.querySelector('#severity'),
      $ruleType: this.shadow.querySelector('#type'),
      $ruleCreatedOn: this.shadow.querySelector('#created-on'),
      $ruleModifiedOn: this.shadow.querySelector('#modified-on'),

      $ruleEntityTypes: this.shadow.querySelector('#entity-types'),
      $mitreIds: this.shadow.querySelector('#mitre-ids'),

      $policies: this.shadow.querySelector('#policies'),
      $ruleSets: this.shadow.querySelector('#rule-sets'),

      $saveButton: this.shadow.querySelector('#saveButton'),
      $cancelButton: this.shadow.querySelector('#cancelButton'),
    };

    this.dom.$learnMoreLink.fontSize = FontSizes.PARAGRAPH_12;
    this.dom.$learnMoreLink.href =
      'https://www.bitdefender.com/business/support/en/77209-214591-integrity-monitoring-default-rules.html';

    this.setLoadingState();
    this.getTranslations();
    this.setTranslatedStrings();
  }

  setLoadingState(): void {
    this.resetTemplateState(this.dom.$wrapper);
    this.setTemplateState(this.dom.$wrapper, TemplateState.LOADING);
  }

  removeLoadingState(): void {
    this.resetTemplateState(this.dom.$wrapper);
  }

  getTranslations() {
    this.commonStrings = this.stringsTranslator.getCommonStrings();
  }

  setTranslatedStrings() {
    this.dom.$generalTitle.innerText = this.dependencies.translateFn('General');
    this.dom.$detailsTitle.innerText = this.dependencies.translateFn('Details');
    this.dom.$configurationTitle.innerText = this.dependencies.translateFn('Configuration');
    this.dom.$assignationTitle.innerText = this.dependencies.translateFn('Assignment');

    this.dom.$learnMoreLink.innerText = this.dependencies.translateFn('Learn more');

    this.dom.$ruleName.label = this.dependencies.translateFn('Rule name');
    this.dom.$ruleDescription.label = this.dependencies.translateFn('Description');
    this.dom.$ruleSeverity.label = this.dependencies.translateFn('Severity');
    this.dom.$ruleType.label = this.dependencies.translateFn('Rule type');
    this.dom.$ruleCreatedOn.label = this.dependencies.translateFn('Created on');
    this.dom.$ruleModifiedOn.label = this.dependencies.translateFn('Last modified on');

    this.dom.$ruleEntityTypes.label = this.dependencies.translateFn('Entity types');
    this.dom.$mitreIds.label = this.dependencies.translateFn('MITRE ATT&CK ID');

    this.dom.$policies.label = this.dependencies.translateFn('Policies');
    this.dom.$ruleSets.label = this.dependencies.translateFn('Rule sets');

    this.dom.$saveButton.innerText = this.dependencies.translateFn('Save#preserve state');
    this.dom.$cancelButton.innerText = this.commonStrings.cancel;
  }

  buildSuccessView() {
    this.createFormGroup();
  }

  buildErrorView(error: Error) {
    console.error(error);
    this.resetTemplateState(this.dom.$wrapper);
    this.setTemplateState(this.dom.$wrapper, TemplateState.ERROR);
  }

  protected onDisconnect() {
    this.unsubscribeObservers();
  }

  public unsubscribeObservers() {
    this.formGroup?.unsubscribe();
    this.formGroupSubscription?.unsubscribe();
  }

  public setFormConfigurability(isConfigurable: boolean, saveButtonHidden = false): void {
    toggleVisibility(this.dom.$configurationSection, isConfigurable);

    if (isConfigurable && !saveButtonHidden) {
      toggleVisibility(this.dom.$saveButton, true);
      this.dom.$cancelButton.textContent = this.commonStrings.cancel;
    } else {
      toggleVisibility(this.dom.$saveButton, false);
      this.dom.$cancelButton.textContent = this.commonStrings.close;
    }
  }

  private createFormGroup() {
    if (!this.formGroup) {
      this.formGroup = new FormGroup({});
    }

    this.formGroup.addFormChild(['stringVariables'], this.dom.$configurationFormFields);

    // Set validators
    this.dom.$configurationFormFields.Validators = [
      RequiredValidator(this.dependencies.translateFn('This field is required.')),
      MaxLengthValidator(
        IMConstants.MAX_LENGTH_CONFIGURABLE_VARIABLES,
        this.dependencies.translateFn('Maximum 256 characters allowed.')
      ),
    ];

    // Enable / disable "Save" button based on formGroup's validity
    this.formGroupSubscription = this.formGroup.valid$.subscribe({
      next: valid => {
        this.dom.$saveButton.disabled = !valid;
      },
    });
  }
}
